import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * ErrorBoundary — Finding 60 of the security audit (HIGH).
 *
 * The app previously had NO error boundary anywhere. One render
 * error in Code99Mode, EPCRPage, or any tab unmounted the whole
 * React tree and dropped the medic to a blank white screen mid-
 * resuscitation. This is the single most consequential resilience
 * gap given the field-use context.
 *
 * Two-layer strategy:
 *   1. Top-level boundary around <Routes/> in App.tsx — last-resort
 *      catch so the chrome (banner + recovery panel) survives.
 *   2. Narrower boundaries around clinical surfaces (EPCRPage,
 *      Code99Mode) so a single tab crash drops back to a recovery
 *      panel WITHOUT unmounting the surrounding context. The medic
 *      keeps their workflow rail, vitals timer, etc.
 *
 * Recovery panel deliberately does NOT call reload() by default —
 * a reload would lose any in-progress form state. Instead it offers
 * a "Recover this view" button that calls setState({hasError:false})
 * to attempt a clean remount, plus a "Full reload" escape hatch.
 *
 * Dexie data is unaffected by any of this — the IndexedDB layer is
 * outside React's render lifecycle, so a crashed tab does NOT
 * orphan an in-flight EPCR write.
 */

interface Props {
  /** Optional name shown in the recovery panel (e.g., "ePCR", "Code 99 workspace"). */
  label?: string;
  /** Optional override panel; defaults to the built-in recovery UI. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, errorCount: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console for the browser devtools / Sentry-style upstream.
    // We do NOT use the audit-log table here (that needs a network +
    // user context that may itself be broken) — surface it locally
    // and let the user choose what to do.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.label ? ` · ${this.props.label}` : ''}] caught render error:`,
      error,
      '\nComponent stack:',
      info.componentStack,
    );
    this.setState((s) => ({ errorCount: s.errorCount + 1 }));
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback && this.state.error) {
      return this.props.fallback(this.state.error, this.reset);
    }

    return <DefaultFallback
      error={this.state.error}
      label={this.props.label}
      errorCount={this.state.errorCount}
      onReset={this.reset}
    />;
  }
}

function DefaultFallback({
  error, label, errorCount, onReset,
}: {
  error: Error | null;
  label?: string;
  errorCount: number;
  onReset: () => void;
}) {
  // After a few back-to-back failures, stop offering the in-place
  // reset (it's clearly stuck) and only offer a full reload. The
  // counter is per-boundary instance so each surface decides on its
  // own when to give up.
  const tooManyResets = errorCount > 2;

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center">
      <div className="flex items-center gap-2 text-amber-400">
        <AlertTriangle size={28} />
        <span className="text-lg font-bold uppercase tracking-widest">Recoverable error</span>
      </div>

      <p className="max-w-md text-sm text-slate-300">
        {label
          ? <>The <strong>{label}</strong> surface hit an unexpected error.</>
          : 'A surface in the app hit an unexpected error.'}{' '}
        Your offline data is safe — nothing has been lost. You can try to recover this view, or
        reload the app entirely.
      </p>

      {error && (
        <details className="max-w-md rounded border border-slate-700 bg-slate-900 px-3 py-2 text-left text-xs text-slate-400">
          <summary className="cursor-pointer font-mono text-slate-300">
            Technical detail
          </summary>
          <pre className="mt-2 whitespace-pre-wrap break-words text-[11px]">{error.message}</pre>
          {error.stack && (
            <pre className="mt-1 whitespace-pre-wrap break-words text-[10px] text-slate-500">
              {error.stack.split('\n').slice(0, 8).join('\n')}
            </pre>
          )}
        </details>
      )}

      <div className="flex flex-wrap items-center justify-center gap-2">
        {!tooManyResets && (
          <button
            type="button"
            onClick={onReset}
            className="btn-primary inline-flex items-center gap-2"
          >
            <RotateCcw size={16} />
            Recover this view
          </button>
        )}
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="btn-secondary inline-flex items-center gap-2"
        >
          Full reload
        </button>
      </div>

      {tooManyResets && (
        <p className="text-xs text-amber-300">
          Repeated recovery attempts failed — full reload required.
        </p>
      )}
    </div>
  );
}
